import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../Hooks/useToast";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import MenuItem from "@mui/material/MenuItem";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import MainHeader from "../../Components/Headres/Main_Header";
import DatagridTable from "../../Components/Table/DataGrid_Table";
import { createColumn } from "../../Components/Table/DataGrid_Cullums";
import useConfirmationModal from "../../Components/Utils/DeleteModal";

import DeleteIcon from "./../../Assets/Icons/Delete.svg";
import Editicon from "./../../Assets/Icons/Edit.svg";

import { fetchGetAllAdminsApi } from "./../../Redux/Admin_Slice";
import { deleteAdminApi } from "../../Apis/Auth/Admin_Login";

const SearchSchema = Yup.object().shape({
  adminName: Yup.string(),
  isDeleted: Yup.string().oneOf(["true", "false"], "Ungültiger Status")
});

const AdminVerwaltung = () => {
  const { toastSuccess, toastError } = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data, isLoading, pagination } = useSelector((state) => state.Admin);

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchFilters, setSearchFilters] = useState({});

  const { openModal: openDeleteModal, ModalComponent } = useConfirmationModal();

  useEffect(() => {
    dispatch(
      fetchGetAllAdminsApi({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        filters: searchFilters
      })
    );
  }, [dispatch, paginationModel, searchFilters]);

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteAdminApi(id);
      if (res.status === 200 || res.ok) {
        toastSuccess("Der Admin wurde erfolgreich gelöscht.");
        dispatch(
          fetchGetAllAdminsApi({
            page: paginationModel.page + 1,
            limit: paginationModel.pageSize,
            filters: searchFilters
          })
        );
      } else {
        toastError("Fehler beim Löschen des Admins.");
      }
    } catch (error) {
      console.error("Delete admin error:", error);
      toastError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    }
  };

  const handleSearch = (values) => {
    const filters = {};
    if (values.adminName) filters.adminName = values.adminName;
    if (values.isDeleted) filters.isDeleted = values.isDeleted;
    setSearchFilters(filters);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleReset = (resetForm) => {
    resetForm();
    setSearchFilters({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const columns = [
    createColumn("adminName", "Name", 220),
    createColumn("email", "Email", 220),
    createColumn("role", "Rolle", 240),
    createColumn("isDeleted", "ist gelöscht", 240),
    createColumn("aktion", "Aktion", 260, "actions", (params) => (
      <Box>
        <IconButton
          onClick={() => navigate(`EditAdmin/${params.row.id}`)}
          color="primary"
          size="small"
        >
          <img style={{ height: "30px" }} src={Editicon} alt="Edit" />
        </IconButton>
        <IconButton color="error" size="small">
          <img
            style={{ height: "30px" }}
            src={DeleteIcon}
            onClick={() => openDeleteModal(() => handleDelete(params.row.id))}
            alt="Delete"
          />
        </IconButton>
      </Box>
    ))
  ];

  const Toolbar = () => (
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2, bgcolor: "#D6B981" }}>
      <Formik
        initialValues={{ adminName: "", isDeleted: "" }}
        validationSchema={SearchSchema}
        onSubmit={handleSearch}
      >
        {({ errors, touched, resetForm }) => (
          <Form style={{ display: "flex", alignItems: "center", gap: 16, width: "100%",justifyContent:"end" }}>
            <Field
              as={TextField}
              name="adminName"
              label="Name"
              variant="outlined"
              size="small"
              error={touched.adminName && Boolean(errors.adminName)}
              helperText={touched.adminName && errors.adminName}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
            <Field
              as={TextField}
              select
              name="isDeleted"
              label="Gelöscht"
              variant="outlined"
              size="small"
              error={touched.isDeleted && Boolean(errors.isDeleted)}
              helperText={touched.isDeleted && errors.isDeleted}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="true">Ja</MenuItem>
              <MenuItem value="false">Nein</MenuItem>
            </Field>
            <Button type="submit" variant="contained" color="inherit" size="medium">
              suchen
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="medium"
              onClick={() => handleReset(resetForm)}
            >
              Aktualisieren
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );

  return (
    <Box>
      <MainHeader onNavigate={() => navigate("CreateAdmin")} title="Neuer Admin" />
      <Box sx={{ height: 600, width: "100%", mt: 3 }}>
        <DatagridTable
          columns={columns}
          rows={data || []}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          rowCount={pagination?.total || 0}
          pageSizeOptions={[5, 10, 25]}
          loading={isLoading}
          toolbarComponent={<Toolbar />}
        />
      </Box>
      <ModalComponent />
    </Box>
  );
};

export default AdminVerwaltung;
